import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook para persistir el estado de formularios en sessionStorage
 * Los datos se guardan automáticamente y se restauran al recargar
 * Se limpian cuando se llama a clearForm() (normalmente tras enviar)
 *
 * @param {string} formKey - Clave única para identificar el formulario
 * @param {object} initialState - Estado inicial del formulario
 * @param {object} options - Opciones adicionales
 * @param {boolean} options.enabled - Si está habilitado (default: true)
 * @param {number} options.debounceMs - Tiempo de debounce para guardar (default: 300ms)
 * @param {function} options.serialize - Función para serializar datos antes de guardar
 * @param {function} options.deserialize - Función para deserializar datos al restaurar
 * @returns {[object, function, function, boolean]} - [formData, setFormData, clearForm, wasRestored]
 */
const useFormPersistence = (formKey, initialState, options = {}) => {
    const {
        enabled = true,
        debounceMs = 300,
        serialize = defaultSerialize,
        deserialize = defaultDeserialize
    } = options;

    const storageKey = `form_draft_${formKey}`;
    const saveTimeoutRef = useRef(null);
    const [wasRestored, setWasRestored] = useState(false);

    // Intentar restaurar datos guardados al inicializar
    const getInitialState = () => {
        if (!enabled) return initialState;

        try {
            const saved = sessionStorage.getItem(storageKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                const deserialized = deserialize(parsed.data, initialState);
                // Verificar que los datos no son muy antiguos (máximo 24 horas)
                const maxAge = 24 * 60 * 60 * 1000;
                if (Date.now() - parsed.timestamp < maxAge) {
                    return deserialized;
                } else {
                    sessionStorage.removeItem(storageKey);
                }
            }
        } catch (error) {
            console.warn('Error restaurando formulario:', error);
            sessionStorage.removeItem(storageKey);
        }
        return initialState;
    };

    const [formData, setFormDataInternal] = useState(getInitialState);

    // Marcar si se restauraron datos
    useEffect(() => {
        if (!enabled) return;

        try {
            const saved = sessionStorage.getItem(storageKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                const maxAge = 24 * 60 * 60 * 1000;
                if (Date.now() - parsed.timestamp < maxAge) {
                    setWasRestored(true);
                }
            }
        } catch (error) {
            // Ignorar errores
        }
    }, [enabled, storageKey]);

    // Guardar datos con debounce
    const saveToStorage = useCallback((data) => {
        if (!enabled) return;

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            try {
                const serialized = serialize(data);
                // No guardar si el formulario está vacío o es igual al estado inicial
                if (!isFormEmpty(serialized, initialState)) {
                    sessionStorage.setItem(storageKey, JSON.stringify({
                        data: serialized,
                        timestamp: Date.now()
                    }));
                }
            } catch (error) {
                console.warn('Error guardando formulario:', error);
            }
        }, debounceMs);
    }, [enabled, storageKey, debounceMs, serialize, initialState]);

    // Wrapper para setFormData que también guarda
    const setFormData = useCallback((newDataOrUpdater) => {
        setFormDataInternal(prevData => {
            const newData = typeof newDataOrUpdater === 'function'
                ? newDataOrUpdater(prevData)
                : newDataOrUpdater;
            saveToStorage(newData);
            return newData;
        });
    }, [saveToStorage]);

    // Función para limpiar el formulario (llamar después de enviar con éxito)
    const clearForm = useCallback(() => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        sessionStorage.removeItem(storageKey);
        setWasRestored(false);
    }, [storageKey]);

    // Limpiar timeout al desmontar
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    return [formData, setFormData, clearForm, wasRestored];
};

// Función por defecto para serializar (excluye archivos y funciones)
function defaultSerialize(data) {
    const serialized = {};
    for (const [key, value] of Object.entries(data)) {
        // Excluir Files, funciones y objetos complejos que no se pueden serializar
        if (value instanceof File || typeof value === 'function') {
            continue;
        }
        // Para arrays de objetos complejos (como Oficinas), guardar solo IDs
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0]?.Id) {
            serialized[key] = value.map(item => ({ Id: item.Id, nombre: item.nombre }));
        } else if (value && typeof value === 'object' && value.Id !== undefined) {
            // Para objetos con Id (como Rol), guardar referencia mínima
            serialized[key] = { Id: value.Id, nombre: value.nombre };
        } else {
            serialized[key] = value;
        }
    }
    return serialized;
}

// Función por defecto para deserializar
function defaultDeserialize(savedData, initialState) {
    // Combinar datos guardados con estado inicial para manejar nuevos campos
    return { ...initialState, ...savedData };
}

// Verificar si el formulario está esencialmente vacío
function isFormEmpty(data, initialState) {
    for (const [key, value] of Object.entries(data)) {
        const initialValue = initialState[key];

        // Ignorar campos que son igual al inicial
        if (JSON.stringify(value) === JSON.stringify(initialValue)) {
            continue;
        }

        // Si hay algún valor diferente al inicial, el formulario no está vacío
        if (value !== '' && value !== null && value !== undefined) {
            if (Array.isArray(value) && value.length === 0) continue;
            return false;
        }
    }
    return true;
}

export default useFormPersistence;
